import { memo, useEffect, useRef } from 'react';
import * as Y from 'yjs';
import { cardsMap, reactionsByEmoji, type Placement } from '../../shared/doc.js';
import { CardBody } from '../cards/CardBody.js';
import { CardErrorBoundary } from '../components.js';
import type { ProjectConn } from '../doc/useProject.js';
import { useYVersion } from '../doc/useY.js';

export interface FrameProps {
  conn: ProjectConn;
  projectId: string;
  cardId: string;
  placement: Placement | null; // null: laid out by its column
  selected: boolean;
  editing: boolean;
  movedBy: { name: string; colour: string } | null;
  onPointerDown: (e: React.PointerEvent, cardId: string) => void;
  onEdit: (cardId: string | null) => void;
  onOpen: (cardId: string) => void;
  onResizeStart: (e: React.PointerEvent, cardId: string) => void;
  onMeasure: (cardId: string, height: number) => void;
}

export const CardFrame = memo(function CardFrame(p: FrameProps) {
  const ref = useRef<HTMLDivElement>(null);
  const cardY = cardsMap(p.conn.doc).get(p.cardId) as Y.Map<any> | undefined;
  useYVersion(cardY);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver(() => p.onMeasure(p.cardId, el.offsetHeight));
    ro.observe(el);
    return () => ro.disconnect();
  }, [p.cardId, p.onMeasure]);

  // Read defensively: a card written by a newer version or a buggy client may lack these fields.
  const commentsY = cardY?.get('comments');
  const comments = commentsY instanceof Y.Array ? commentsY.length : 0;
  const reactionsY = cardY?.get('reactions');
  const reactions = reactionsY instanceof Y.Map ? reactionsByEmoji(reactionsY.toJSON() as Record<string, true>) : [];
  const createdBy = cardY?.get('createdBy') as { kind?: string; name?: string } | undefined;
  const style: React.CSSProperties = p.placement
    ? { position: 'absolute', left: p.placement.pos.x, top: p.placement.pos.y, width: p.placement.size.w, zIndex: p.placement.z }
    : {};
  return (
    <div
      ref={ref}
      className={`card-frame${p.selected ? ' selected' : ''}${p.editing ? ' editing' : ''}${p.movedBy ? ' moved' : ''}`}
      style={{ ...style, ...(p.movedBy ? { outlineColor: p.movedBy.colour } : {}) }}
      data-card-id={p.cardId}
      data-kind={cardY?.get('kind')}
      onPointerDown={(e) => !p.editing && p.onPointerDown(e, p.cardId)}
      onDoubleClick={(e) => {
        e.stopPropagation();
        p.onEdit(p.cardId);
      }}
    >
      {p.movedBy && (
        <span className="moved-by" style={{ background: p.movedBy.colour }}>
          {p.movedBy.name}
        </span>
      )}
      <CardErrorBoundary>
        <CardBody conn={p.conn} projectId={p.projectId} cardId={p.cardId} editing={p.editing} onDoneEditing={() => p.onEdit(null)} />
      </CardErrorBoundary>
      <footer className="card-meta">
        {createdBy?.kind === 'agent' && <span className="by-agent">🤖 {createdBy.name}</span>}
        {reactions.map((r) => (
          <span key={r.emoji} className="reaction-count">
            {r.emoji} {r.userIds.length}
          </span>
        ))}
        {comments > 0 && <span className="comment-count">💬 {comments}</span>}
        <button className="open-card" aria-label="Open card" onPointerDown={(e) => e.stopPropagation()} onClick={() => p.onOpen(p.cardId)}>
          ⤢
        </button>
      </footer>
      {p.placement && <div className="resize-handle" aria-hidden onPointerDown={(e) => p.onResizeStart(e, p.cardId)} />}
    </div>
  );
});
